import { supabase } from '../lib/supabase';

export const notificationService = {
  /**
   * Subscribe to real-time AI recommendation updates
   * @param {Function} onInsert - Callback when new recommendation is created
   * @param {Function} onUpdate - Callback when recommendation is updated
   * @param {Function} onDelete - Callback when recommendation is deleted
   * @returns {Object} Supabase channel for cleanup
   */
  subscribeToRecommendations(onInsert, onUpdate, onDelete) {
    const channel = supabase?.channel('ai_recommendations_changes')?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'ai_recommendations'
        },
        (payload) => {
          if (onInsert) {
            onInsert(payload?.new);
          }
        }
      )?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'ai_recommendations'
        },
        (payload) => {
          if (onUpdate) {
            onUpdate(payload?.new, payload?.old);
          }
        }
      )?.on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'ai_recommendations'
        },
        (payload) => {
          if (onDelete) {
            onDelete(payload?.old);
          }
        }
      )?.subscribe();

    return channel;
  },

  /**
   * Unsubscribe from real-time updates
   * @param {Object} channel - Supabase channel to remove
   */
  unsubscribe(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  },

  /**
   * Get all AI recommendations for a client
   * @param {string} clientId - User ID
   * @returns {Promise<Array>} List of recommendations
   */
  async getRecommendations(clientId) {
    const { data, error } = await supabase?.from('ai_recommendations')?.select('*')?.eq('client_id', clientId)?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      clientId: row?.client_id,
      serviceName: row?.service_name,
      category: row?.category,
      rationale: row?.rationale,
      predictedROI: row?.predicted_roi,
      roiConfidence: row?.roi_confidence,
      implementationMonths: row?.implementation_months,
      costRange: row?.cost_range,
      successMetrics: row?.success_metrics,
      riskFactors: row?.risk_factors,
      status: row?.status,
      reviewedBy: row?.reviewed_by,
      reviewedAt: row?.reviewed_at,
      reviewNotes: row?.review_notes,
      aiAnalysis: row?.ai_analysis,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  },

  /**
   * Get all pending recommendations (for consultants/admins)
   * @returns {Promise<Array>} List of pending recommendations
   */
  async getPendingRecommendations() {
    const { data, error } = await supabase?.from('ai_recommendations')?.select(`
        *,
        client:user_profiles!ai_recommendations_client_id_fkey(
          full_name,
          email,
          company
        )
      `)?.eq('status', 'pending_review')?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      clientId: row?.client_id,
      clientName: row?.client?.full_name,
      clientEmail: row?.client?.email,
      clientCompany: row?.client?.company,
      serviceName: row?.service_name,
      category: row?.category,
      rationale: row?.rationale,
      predictedROI: row?.predicted_roi,
      roiConfidence: row?.roi_confidence,
      implementationMonths: row?.implementation_months,
      costRange: row?.cost_range,
      successMetrics: row?.success_metrics,
      riskFactors: row?.risk_factors,
      status: row?.status,
      aiAnalysis: row?.ai_analysis,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  },

  /**
   * Create new AI recommendation in database
   * @param {Object} recommendation - Recommendation data
   * @returns {Promise<Object>} Created recommendation
   */
  async createRecommendation(recommendation) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('ai_recommendations')?.insert({
        client_id: user?.id,
        service_name: recommendation?.serviceName,
        category: recommendation?.category,
        rationale: recommendation?.rationale,
        predicted_roi: recommendation?.predictedROI,
        roi_confidence: recommendation?.roiConfidence,
        implementation_months: recommendation?.implementationMonths,
        cost_range: recommendation?.costRange,
        success_metrics: recommendation?.successMetrics,
        risk_factors: recommendation?.riskFactors,
        ai_analysis: recommendation?.aiAnalysis,
        status: 'pending_review'
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      clientId: data?.client_id,
      serviceName: data?.service_name,
      category: data?.category,
      rationale: data?.rationale,
      predictedROI: data?.predicted_roi,
      roiConfidence: data?.roi_confidence,
      implementationMonths: data?.implementation_months,
      costRange: data?.cost_range,
      successMetrics: data?.success_metrics,
      riskFactors: data?.risk_factors,
      status: data?.status,
      aiAnalysis: data?.ai_analysis,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  /**
   * Update recommendation status (approval workflow)
   * @param {string} recommendationId - Recommendation ID
   * @param {string} status - New status
   * @param {string} reviewNotes - Optional review notes
   * @returns {Promise<Object>} Updated recommendation
   */
  async updateRecommendationStatus(recommendationId, status, reviewNotes = null) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const updateData = {
      status,
      reviewed_by: user?.id,
      reviewed_at: new Date()?.toISOString()
    };

    if (reviewNotes) {
      updateData.review_notes = reviewNotes;
    }

    const { data, error } = await supabase?.from('ai_recommendations')?.update(updateData)?.eq('id', recommendationId)?.select(`
        *,
        client:user_profiles!ai_recommendations_client_id_fkey(
          full_name,
          email
        )
      `)?.single();

    if (error) throw error;

    // If status is approved, trigger email notification
    if (status === 'approved' && data?.client) {
      try {
        await this.sendApprovalNotification({
          recommendation: {
            serviceName: data?.service_name,
            category: data?.category,
            rationale: data?.rationale,
            predictedROI: data?.predicted_roi,
            roiConfidence: data?.roi_confidence,
            implementationMonths: data?.implementation_months,
            costRange: data?.cost_range,
            successMetrics: data?.success_metrics,
            riskFactors: data?.risk_factors
          },
          clientEmail: data?.client?.email,
          clientName: data?.client?.full_name
        });
      } catch (emailError) {
        console.error('Failed to send approval notification email:', emailError);
        // Don't throw - email failure shouldn't block status update
      }
    }

    return {
      id: data?.id,
      status: data?.status,
      reviewedBy: data?.reviewed_by,
      reviewedAt: data?.reviewed_at,
      reviewNotes: data?.review_notes,
      updatedAt: data?.updated_at
    };
  },

  /**
   * Send approval notification email via Edge Function
   * @param {Object} payload - Notification payload
   * @returns {Promise<Object>} Email send result
   */
  async sendApprovalNotification(payload) {
    const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
    if (!supabaseUrl) {
      throw new Error('VITE_SUPABASE_URL not configured');
    }

    const { data: { session } } = await supabase?.auth?.getSession();
    if (!session) {
      throw new Error('No active session');
    }

    const response = await fetch(`${supabaseUrl}/functions/v1/send-recommendation-notification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token}`
      },
      body: JSON.stringify(payload)
    });

    if (!response?.ok) {
      const errorText = await response?.text();
      throw new Error(`Failed to send notification: ${errorText}`);
    }

    return await response?.json();
  }
};
function newsletterService(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: newsletterService is not implemented yet.', args);
  return null;
}

export { newsletterService };